import React, { useContext } from "react";
import clsx from "clsx";
import Head from "next/head";
import { useSelector } from "react-redux";
import { ArticleImage } from "@bloomberg/media-ui-article";
import { makeSrcset } from "@bloomberg/media-ui-core";

import { selectStories } from "~redux/features/stories-slice";
import { Image } from "~types/types";
import { ANIMATED_GIF } from "~utils";
import { CurrentStoryContext } from "~utils/currentStoryContext";

import styles from "./ledeImage.module.css";

interface LedeImageProps {
    lede: Image;
    className?: string;
    isExpandable?: boolean;
    maxWidth?: number;
}

export const DEFAULT_NATIVE_WIDTH = 1200;

const LedeImage = ({
    lede,
    className,
    isExpandable = false,
    maxWidth = 1200,
}: LedeImageProps) => {
    const { caption, credit, width, height, alt } = lede;
    let { url } = lede;
    const { story } = useContext(CurrentStoryContext);
    if (ANIMATED_GIF.test(url)) {
        url = url
            .replace(/^http:\/\//, "//")
            .replace(/^\/\//, "https://")
            .replace(/.webp/, ".gif");
    }

    const stories = useSelector(selectStories);
    const isFirstStory = stories[0]?.slug === story.slug;

    const derivedAspectRatio = width / height;

    const renderedWidth = width || DEFAULT_NATIVE_WIDTH;
    const renderedHeight = Math.round(renderedWidth / derivedAspectRatio);

    const imageSrcSet = makeSrcset(url, renderedWidth, renderedHeight);
    const imageSizes = `(min-width: 1px) and (max-width: 619px) 400px,
                        (min-width: 620px) and (max-width: 759px) 600px,
                        (min-width: 760px) and (max-width: 1019px) 800px, ${maxWidth}px`;

    return (
        <div className={clsx(styles.ledeImage, className)}>
            <ArticleImage
                src={url}
                credit={credit}
                caption={caption}
                altText={alt}
                fetchpriority="high"
                isLazy={false}
                aspectRatio={derivedAspectRatio}
                srcSet={imageSrcSet}
                sizes={imageSizes}
                isExpandable={isExpandable}
            />
            {isFirstStory && (
                // Note how we omit the `href` attribute, as it would only be relevant
                // for browsers that do not support `imagesrcset`, and in those cases
                // it would likely cause the incorrect image to be preloaded.
                //
                // https://html.spec.whatwg.org/multipage/semantics.html#attr-link-imagesrcset
                <Head>
                    <link
                        key={`__nimg-${url}${imageSrcSet}${imageSizes}`}
                        rel="preload"
                        as="image"
                        href={imageSrcSet ? undefined : url}
                        imageSizes={imageSizes}
                        imageSrcSet={imageSrcSet}
                        // eslint-disable-next-line
                        // @ts-ignore
                        onLoad="this.rel='image';this.removeAttribute('onload')"
                        fetchPriority="high"
                    />
                </Head>
            )}
        </div>
    );
};

export default LedeImage;
